import { Fragment, useLayoutEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loading, NotFound } from '../components/layout'
import { useBoardState, useStore } from '../store/context'
import { movedProjectPath, projectForPath, sessionPath } from './routing'

/** Select before mounting a page, so no read or mutation uses the previous history entry's project. Today's (projects/ProjectRoute.tsx there). */
export function ProjectRoute({ children }: { children: ReactNode }) {
  const { pathname, search, hash } = useLocation()
  const { projects, selectedProjectId } = useBoardState()
  const store = useStore()
  const all = [...projects.values()]
  const project = projectForPath(pathname, all)
  useLayoutEffect(() => {
    if (project && project.id !== selectedProjectId) store.selectProject(project.id)
  }, [project, selectedProjectId, store])
  const moved = movedProjectPath(pathname, search, hash, all, selectedProjectId)
  if (moved) return <Navigate to={moved} replace />
  // A workspace with no project yet shows its pages' empty states at any project's address: one signed in since, in another tab, may have none.
  if (/^\/p\//i.test(pathname) && !project && all.length > 0) return <NotFound what="page" />
  if (project && project.id !== selectedProjectId) return <Loading />
  return <Fragment key={project?.id}>{children}</Fragment>
}

/** A session's page, in its project's Code tab; `/sessions/<id>` while its project is not known. */
export function useSessionPath(): (session: string) => string {
  const { sessions, projects } = useBoardState()
  return (id) => {
    const key = projects.get(sessions.get(id)?.project_id ?? '')?.key
    return key ? sessionPath(key, id) : `/sessions/${encodeURIComponent(id)}`
  }
}
